'use client';

import { useRef, type PropsWithChildren } from 'react';

import { createContext } from '@backyard-ui/utils';
import { createStore, type StoreApi } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { type Response as GetServiceInfoResponse } from '@/infra/services/catalog/service/get-service-info';

export interface ServiceState {
  service: GetServiceInfoResponse['data']['product'];
}

interface ServiceStateProviderProps extends PropsWithChildren {
  data: ServiceState;
}

const { Provider, useContext } = createContext<StoreApi<ServiceState>>({
  name: '@store/catalog/service',
});

export function useServiceState<T>(selector: (state: ServiceState) => T) {
  const state = useContext();

  return useStoreWithEqualityFn(state, selector, shallow);
}

export function ServiceStateProvider(
  props: Readonly<ServiceStateProviderProps>
) {
  const { children, data } = props;

  const storeRef = useRef(createStore<ServiceState>()(() => data));

  return <Provider value={storeRef.current}>{children}</Provider>;
}
